import React from 'react';
import axios from 'axios';
import { GitMerge, X, ClipboardPaste, Mic, MicOff, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

const API = window.__WR_API_BASE__ || '';

/**
 * MergeNoteModal — full-size modal for pasting a teammate's notes/dictation
 * to merge into the active encounter input. Replaces the legacy `window.prompt`
 * which was too small to paste long notes from MHS Genesis.
 *
 * Supports the "relay race" workflow: open → paste → append → continue working.
 * Can be re-opened any number of times to append additional content (e.g.,
 * Genesis copy-paste added later in the encounter).
 *
 * Feb 14, 2026 — replaced the in-browser Web Speech API mic with the SAME
 * Deepgram Nova-3 pipeline the main screen uses (POST /api/transcribe).
 * Web Speech was missing 70%+ of the audio and the user explicitly requested
 * parity with the main mic. Workflow:
 *   tap mic → recording (MediaRecorder webm/opus) → tap stop → POST blob →
 *   Deepgram transcript → appended to the textarea.
 */
const MergeNoteModal = ({ open, onClose, onMerge, currentNotes }) => {
  const [text, setText] = React.useState('');
  const [isRecording, setIsRecording] = React.useState(false);
  const [isTranscribing, setIsTranscribing] = React.useState(false);
  const [recordingSeconds, setRecordingSeconds] = React.useState(0);
  const textareaRef = React.useRef(null);
  const mediaRecorderRef = React.useRef(null);
  const audioChunksRef = React.useRef([]);
  const recordingTimerRef = React.useRef(null);

  React.useEffect(() => {
    if (open && textareaRef.current) {
      const t = setTimeout(() => textareaRef.current?.focus(), 100);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Esc to close
  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Stop recording + clean up on close / unmount
  React.useEffect(() => {
    if (!open && isRecording) {
      stopRecording(true); // silent stop
    }
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Pick the best supported mime type (matches useRecording.js logic)
      let mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        if (MediaRecorder.isTypeSupported('audio/mp4')) mimeType = 'audio/mp4';
        else if (MediaRecorder.isTypeSupported('audio/ogg')) mimeType = 'audio/ogg';
        else mimeType = '';
      }
      const rec = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      audioChunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        // Always release the mic stream
        try { stream.getTracks().forEach(t => t.stop()); } catch { /* ignore */ }
        const finalType = rec.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type: finalType });
        audioChunksRef.current = [];
        if (blob.size < 1024) {
          toast.error('No audio captured — check your mic permissions');
          return;
        }
        await transcribeBlob(blob);
      };
      rec.onerror = (e) => {
        toast.error(`Recording error: ${e?.error || 'unknown'}`);
        setIsRecording(false);
      };
      mediaRecorderRef.current = rec;
      rec.start();
      setIsRecording(true);
      setRecordingSeconds(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds(s => s + 1);
      }, 1000);
      toast.success('Recording — speak clearly. Tap stop when done.');
    } catch (e) {
      const msg = e?.name === 'NotAllowedError'
        ? 'Mic permission denied — enable in browser settings'
        : `Could not access mic: ${e?.message || 'unknown'}`;
      toast.error(msg);
      setIsRecording(false);
    }
  };

  const stopRecording = (silent = false) => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    } catch { /* ignore */ }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setIsRecording(false);
    if (!silent) {
      // Loading state will be set inside transcribeBlob via onstop
    }
  };

  const transcribeBlob = async (blob) => {
    setIsTranscribing(true);
    try {
      const ext = (blob.type || 'audio/webm').split('/')[1].split(';')[0];
      const filename = `merge-dictation.${ext === 'mpeg' ? 'mp3' : ext}`;
      const formData = new FormData();
      formData.append('file', blob, filename);
      const response = await axios.post(`${API}/api/transcribe`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
        withCredentials: true,
      });
      const transcript = (response?.data?.transcript || response?.data?.text || '').trim();
      if (!transcript) {
        toast.error('Empty transcript — try again, speak more clearly');
        return;
      }
      // Append to existing text with a space separator
      setText((prev) => {
        const cur = (prev || '').trim();
        return cur ? `${cur} ${transcript}` : transcript;
      });
      toast.success(`Transcribed ${transcript.split(/\s+/).length} words`);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || 'unknown';
      if (status === 500 && /deepgram/i.test(detail)) {
        toast.error('Transcription service unavailable — paste manually');
      } else {
        toast.error(`Transcription failed: ${detail}`);
      }
    } finally {
      setIsTranscribing(false);
    }
  };

  const toggleRecording = () => {
    if (isRecording) stopRecording(false);
    else startRecording();
  };

  const handlePasteFromClipboard = async () => {
    try {
      const clip = await navigator.clipboard.readText();
      if (clip && clip.trim()) {
        setText((prev) => (prev ? `${prev}\n\n${clip}` : clip));
        toast.success('Pasted from clipboard');
      } else {
        toast.info('Clipboard is empty');
      }
    } catch (e) {
      toast.error('Clipboard access denied — paste manually with Ctrl/Cmd+V');
    }
  };

  const handleMerge = () => {
    if (isRecording) stopRecording(true);
    const trimmed = text.trim();
    if (!trimmed) {
      toast.error('Nothing to merge — paste your teammate\'s notes first');
      return;
    }
    onMerge(trimmed);
    setText('');
    onClose();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="merge-modal-backdrop"
    >
      <div
        className="bg-card border border-amber-500/30 rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="merge-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <GitMerge className="w-5 h-5 text-amber-400" />
            <h2 className="text-base font-semibold text-foreground">
              Merge Teammate Notes
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                ({currentNotes?.trim() ? 'append to current input' : 'becomes the new input'})
              </span>
            </h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8"
            data-testid="merge-modal-close"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          <p className="text-xs text-muted-foreground leading-relaxed">
            Paste your teammate's notes, dictation, or any content from MHS Genesis below.
            This will append to your current input (separated by <code className="px-1 py-0.5 bg-secondary rounded text-[10px]">--- TEAMMATE INPUT ---</code> marker)
            so you can pass the baton forward in the encounter without losing your own context.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePasteFromClipboard}
              className="h-8 text-xs gap-1.5 border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
              data-testid="merge-modal-paste-clipboard"
              disabled={isRecording || isTranscribing}
            >
              <ClipboardPaste className="w-3.5 h-3.5" />
              Paste from clipboard
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={toggleRecording}
              disabled={isTranscribing}
              className={`h-8 text-xs gap-1.5 ${
                isRecording
                  ? 'border-red-500/60 bg-red-500/10 text-red-400 animate-pulse'
                  : isTranscribing
                    ? 'border-blue-500/60 bg-blue-500/10 text-blue-400'
                    : 'border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10'
              }`}
              title={isRecording ? 'Stop recording and transcribe' : 'Voice dictation (Deepgram Nova-3 — same as main mic)'}
              data-testid="merge-modal-dictate"
            >
              {isTranscribing
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : isRecording
                  ? <MicOff className="w-3.5 h-3.5" />
                  : <Mic className="w-3.5 h-3.5" />}
              {isTranscribing
                ? 'Transcribing…'
                : isRecording
                  ? `Stop · ${Math.floor(recordingSeconds / 60)}:${String(recordingSeconds % 60).padStart(2, '0')}`
                  : 'Dictate'}
            </Button>
            <span className="text-[10px] text-muted-foreground">
              {isRecording ? 'Recording with Deepgram — speak clearly' : 'or paste with Ctrl/Cmd+V'}
            </span>
          </div>
          <Textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste teammate's notes, MHS Genesis content, or click 'Dictate' to speak directly..."
            className={`w-full min-h-[400px] text-sm bg-input border focus:ring-1 font-mono ${
              isRecording
                ? 'border-red-500/50 focus:border-red-500/70 focus:ring-red-500/20'
                : isTranscribing
                  ? 'border-blue-500/50 focus:border-blue-500/70 focus:ring-blue-500/20'
                  : 'border-border focus:border-amber-500/50 focus:ring-amber-500/20'
            }`}
            data-testid="merge-modal-textarea"
            disabled={isTranscribing}
          />
          <div className="flex items-center justify-between text-[10px] text-muted-foreground/70">
            <span>{text.length} characters · {text.split(/\s+/).filter(Boolean).length} words</span>
            {isRecording && (
              <span className="flex items-center gap-1 text-red-400 font-medium">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                LIVE — Deepgram Nova-3
              </span>
            )}
            {isTranscribing && (
              <span className="flex items-center gap-1 text-blue-400 font-medium">
                <Loader2 className="w-3 h-3 animate-spin" />
                Transcribing audio…
              </span>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border bg-secondary/20">
          <Button
            variant="ghost"
            onClick={onClose}
            className="h-9 text-sm"
            data-testid="merge-modal-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleMerge}
            disabled={!text.trim() || isRecording || isTranscribing}
            className="h-9 text-sm font-semibold gap-2 bg-amber-500/90 hover:bg-amber-500 text-amber-950 disabled:opacity-40"
            data-testid="merge-modal-confirm"
          >
            <GitMerge className="w-4 h-4" />
            Merge into Notes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MergeNoteModal;
